import React, { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { MetricCard } from "../ui/MetricCard";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import Tooltip from "@mui/material/Tooltip";
import { ListFilter } from "lucide-react";
import "./SubdomainDetail.scss";
import Layer1 from "../../../assests/Layer_1.png";
import group1 from "../../../assests/Vector.png";
import circle from "../../../assests/Group3702.png";
import ProductLogo from "../../ProductLogo/ProductLogo";
import LogoImage from "../../ProductLogo/LogoImage";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";
import { buildDataDomainUrl } from "../../../utils/dataDomainUrl";
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import AppliedFilters from "../../common/AppliedFilters";

const DATA_LAYER_OPTIONS = [
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "ingestion", label: "Ingestion" },
  { value: "bronze", label: "Bronze" },
  { value: "analyticalProduct", label: "Analytical Product" },
];

export const ProductPage = ({
  subdomain,
  onBack,
  onL4Click,
  regions = [],
  appliedFilters,
  onFilterChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const rowsPerPage = 30;

  const filterSections = useMemo(() => [
    {
      fields: [
        {
          type: "multiselect",
          key: "regions",
          label: "Region",
          options: regions.map((r) => ({ value: r, label: r })),
        },
        {
          type: "multiselect",
          key: "dataLayer",
          label: "Data Layer",
          options: DATA_LAYER_OPTIONS,
        },
      ],
    },
  ], [regions]);

  const filtersApplied = (appliedFilters?.regions?.length || 0) > 0 || (appliedFilters?.dataLayer?.length || 0) > 0;
  const activeFilterCount = (appliedFilters?.regions?.length || 0) + (appliedFilters?.dataLayer?.length || 0);

  const appliedChips = [
    ...(appliedFilters?.regions || []).map((r) => ({ label: "Region", value: r })),
    ...(appliedFilters?.dataLayer || []).map((l) => ({
      label: "Data Layer",
      value: DATA_LAYER_OPTIONS.find((o) => o.value === l)?.label || l,
    })),
  ];

  const [searchParams] = useSearchParams();

  const currentL1 = searchParams.get("l1");
  const currentL2 = searchParams.get("l2");
  const currentL3 = searchParams.get("l3");

  const filteredRows =
    subdomain?.Products?.filter((sub) =>
      sub.product_name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];
  console.log("filteredRows", filteredRows);

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="space-y-8">
      {/* Subdomain Summary */}
      <div className="rounded-lg p-4 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-1">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-black mb-4">{subdomain.name}</h1>

          </div>
          <div className="flex flex-col md:flex-row items-center space-x-3 gap-3 mt-2 md:mt-0 flex-shrink-0">
            <button
              onClick={onBack}
              className="h-[36px] text-[#FF03F3] hover:text-white transition-colors duration-200 border border-[#FF03F3] p-2 rounded-lg flex items-center gap-2 hover:bg-[#FF03F3] hover:shadow-lg px-4 whitespace-nowrap"
            >
              <FaArrowLeft /> Go Back
            </button>
          </div>
        </div>
        <p className="text-lg mb-3 text-black leading-relaxed">
              {subdomain.description}
            </p>
            <div className="mt-2 mb-4">
            <ERLandingPage />
            </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Total Product"
            value={subdomain?.total_products ? subdomain?.total_products : "0"}
            variant="success"
          />

          <MetricCard
            title="Quality Score"
            value={subdomain?.score ? subdomain?.score : "0%"}
            variant="warning"
          />
          <MetricCard
            title="Total Assets"
            value={subdomain?.total_assets ? subdomain?.total_assets : "0"}
            variant="primary"
          />
        </div>
      </div>

      {/* Products Section - Always show to display empty state */}
      {subdomain && (
        <div className="space-y-6">
          {/* Search and Filter Controls */}
          <div className="space-y-4">
            {/* First Row: Title, Search, Filters button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">Products</h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search Products..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-[45px]"
                  />
                </div>
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white border-[#bb1ccc] hover:bg-[#4f52d1]"
                >
                  <ListFilter className="h-4 w-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          <AppliedFilters chips={appliedChips} />

          {/* Empty State Message */}
          {currentRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-center">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Products Found
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {filtersApplied
                    ? "No products match your current filter criteria. Try adjusting your filters."
                    : "There are no products available at this time."}
                </p>
                {filtersApplied && (
                  <button
                    onClick={() => {
                      if (onFilterChange) {
                        onFilterChange([], []);
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#FF03F3] hover:bg-[#000080] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF03F3]"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Results Grid */}
          {currentRows.length > 0 && (
            <div className="subdomain-gridbox">
              {currentRows.map((l3Subdomain) => {
                const href = buildDataDomainUrl({
                  selectedL1: currentL1,
                  selectedL2: currentL2,
                  selectedL3: currentL3,
                  selectedL4: l3Subdomain.product_name,
                });

                const isL3Empty = parseInt(l3Subdomain.product_assets || 0) === 0;

                const handleClick = (e) => {
                  if (isL3Empty) {
                    e.preventDefault();
                    return;
                  }
                  const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
                  if (isModifiedClick) return;
                  e.preventDefault();
                  onL4Click(l3Subdomain.product_name);
                };

                return (
                  <Link
                    key={l3Subdomain.name}
                    to={isL3Empty ? '#' : href}
                    onClick={handleClick}
                    style={{ pointerEvents: isL3Empty ? 'none' : 'auto' }}
                    className={`bg-white rounded-lg border border-gray-200 transition-all duration-200 ${
                      !isL3Empty ? "cursor-pointer hover:shadow-lg" : ""
                    }`}
                  >
                    <div
                      style={{ width: "100%" }}
                      className="flex justify-between bg-gray-100 border-b border-gray-200 p-4"
                    >
                      <div className="flex-1">
                        <div className="flex justify-between items-center w-full">
                          <div
                            style={{ display: "grid" }}
                            className="items-center gap-1"
                          >
                            <div className="text-xl font-semibold text-gray-900 case capitalize flex items-center">
                              <Tooltip
                                title="Data Steward Certified"
                                placement="top"
                              >
                                <img src={Layer1} className="mr-[5px]" />
                              </Tooltip>
                              <span className="truncate max-w-[200px] block">{l3Subdomain.product_name}</span>
                            </div>

                            <p
                              className="text-black mb-1 text-sm  pl-2 pr-4 line-clamp-1  overflow"
                              title={l3Subdomain.product_description}
                            >
                              {l3Subdomain.product_description ||
                                "Not Available"}
                            </p>

                            <div className="flex items-center gap-2 justify-start mt-1">
                              <div className="text-gray-500 h-[10px]"></div>
                              {(l3Subdomain.product_layer?.toLowerCase() === "bronze"
                                || l3Subdomain.product_layer?.toLowerCase() === "ingestion")
                              && // <ProductLogo productId={l3Subdomain.product_id} />
                                <div className="ml-1 inline-flex items-center gap-2">
                                  {l3Subdomain.logoIds?.map((l) => (
                                    <LogoImage key={l} logoId={l} />
                                  ))}
                                </div>
                              }
                              <div
                                style={{
                                  fontSize: "10px",
                                  background:
                                    l3Subdomain.product_layer?.toLowerCase() ===
                                    "gold"
                                      ? "#FFD700"
                                      : l3Subdomain.product_layer?.toLowerCase() ===
                                          "silver"
                                        ? "#C0C0C0"
                                        : l3Subdomain.product_layer?.toLowerCase() ===
                                            "bronze"
                                          ? "#CD7F32"
                                          : "#e0e0e0",
                                  color:
                                    l3Subdomain.product_layer?.toLowerCase() ===
                                    "gold"
                                      ? "#8B8000"
                                      : l3Subdomain.product_layer?.toLowerCase() ===
                                          "silver"
                                        ? "#4F4F4F"
                                        : l3Subdomain.product_layer?.toLowerCase() ===
                                            "bronze"
                                          ? "#ffffff"
                                          : "#000000",
                                }}
                                className="py-1 px-3 rounded-xl capitalize"
                              >
                                {l3Subdomain.product_layer || "N/A"}
                              </div>
                            </div>
                          </div>

                          {l3Subdomain &&
                            l3Subdomain.product_assets &&
                            l3Subdomain.product_assets > 0 && (
                              <div>
                                <FaAngleRight
                                  style={{
                                    fontSize: "20px",
                                    cursor: "pointer",
                                  }}
                                  className="text-gray-400"
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-2  pl-4 pr-4 pt-[20px] pb-1 ">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 rounded-lg">
                          <img
                            src={circle}
                            className="bg-[#FF2897] p-3 h-[50px]"
                            alt="icon"
                          />
                        </div>
                        <div>
                          <div
                            style={{ fontSize: "13px" }}
                            className="text-black"
                          >
                            Assets
                          </div>
                          <div className="font-semibold text-sm text-gray-900">
                            {l3Subdomain.product_assets}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="p-3 rounded-lg">
                          <img
                            src={group1}
                            className="bg-[#63B945] p-3 h-[50px] "
                          />
                        </div>
                        <div>
                          <div
                            style={{ fontSize: "13px" }}
                            className="text-black"
                          >
                            CDE
                          </div>
                          <div className="font-semibold text-sm text-gray-900">
                            {l3Subdomain.product_cde_count}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quality Score Bar */}
                    <div className="mt-2 p-4 ">
                      <div className="flex items-center justify-between  mb-1">
                        <span className="text-black">Quality Score</span>
                        <span className="text-black">
                          {l3Subdomain.product_score}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            parseFloat(l3Subdomain.product_score) > 95
                              ? "bg-green-700"
                              : parseFloat(l3Subdomain.product_score) < 90
                                ? "bg-red-500"
                                : parseFloat(l3Subdomain.product_score) > 90
                                  ? "bg-yellow-700"
                                  : "bg-gray-700"
                          }`}
                          style={{
                            width: `${parseFloat(l3Subdomain.product_score)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pagination - only show when there's data */}
      {currentRows.length > 0 && (
        <div className="pagination flex flex-wrap md:flex-nowrap justify-between items-center gap-3 mt-4 px-4">
          <span className="text-sm text-gray-600">
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, totalRows)} of {totalRows} entries
          </span>
          <div className="pagination-controls flex flex-wrap gap-1">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={`px-3 py-1 border rounded transition-colors duration-200 ${
                  currentPage === idx + 1
                    ? "bg-blue-500 text-white"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{ regions: appliedFilters?.regions || [], dataLayer: appliedFilters?.dataLayer || [] }}
        onApply={(filters) => onFilterChange(filters.regions || [], filters.dataLayer || [])}
        onReset={() => onFilterChange([], [])}
        flat
        subtitle="Filter by region & data layer"
      />
    </div>
  );
};
